import i18nFactory from '@yandex-int/i18n';
import moment from 'moment';

import { getColumnsSettings } from '../../declarations/common';
import { mapDateRangePresetToDatePeriod } from '../../mappers/common/common';
import { logs } from '../../typings/common';
import { Criteria, FilterExpressionValueType, FilterPartialExpression, Operator } from '../../typings/filter';
import { OutputCategory } from '../../typings/graphql-log';
import {
    Category,
    changedValueKey,
    defaultChangedValueWidth,
    LogEventCategories,
    LogNavEntityParams,
    LogParams,
    LogPeriod
} from '../../typings/log';
import { LogState } from '../../typings/state';
import { LocalStorage, StorageKeyType } from '../Storage/Storage';

import * as keyset from './LogHelpers.i18n';

const i18n = i18nFactory(keyset);

export class LogHelpers {
    public static noCorrectionValue = 100;

    private static categoryNames: Record<Category, string> = {
        [OutputCategory.ADGROUP_DEMOGRAPHY_MULTIPLIERS]: i18n('adgroup-demography-multipliers'),
        [OutputCategory.ADGROUP_MOBILE_MULTIPLIER]: i18n('adgroup-mobile-multiplier'),
        [OutputCategory.ADGROUP_PERFORMANCE_TGO_MULTIPLIER]: i18n('adgroup-performance-tgo-multiplier'),
        [OutputCategory.ADGROUP_RETARGETING_MULTIPLIERS]: i18n('adgroup-retargeting-multipliers'),
        [OutputCategory.ADGROUP_VIDEO_MULTIPLIER]: i18n('adgroup-video-multiplier'),
        [OutputCategory.ADGROUP_REGIONS]: i18n('adgroup-regions'),
        [OutputCategory.BANNERS_ARCHIVED]: i18n('banners-archived'),
        [OutputCategory.BANNERS_HIDE]: i18n('banners-hide'),
        [OutputCategory.BANNERS_SHOW]: i18n('banners-show'),
        [OutputCategory.BANNERS_UNARCHIVED]: i18n('banners-unarchived'),
        [OutputCategory.CAMPAIGN_ARCHIVED]: i18n('campaign-archived'),
        [OutputCategory.CAMPAIGN_BROAD_MATCH]: i18n('campaign-broad-match'),
        [OutputCategory.CAMPAIGN_CREATION]: i18n('campaign-creation'),
        [OutputCategory.CAMPAIGN_DEMOGRAPHY_MULTIPLIERS]: i18n('campaign-demography-multipliers'),
        [OutputCategory.CAMPAIGN_DISABLED_DOMAINS]: i18n('campaign-disabled-domains'),
        [OutputCategory.CAMPAIGN_DISABLED_IPS]: i18n('campaign-disabled-ips'),
        [OutputCategory.CAMPAIGN_DISABLED_SSP]: i18n('campaign-disabled-ssp'),
        [OutputCategory.CAMPAIGN_EXTENDED_GEOTARGETING]: i18n('campaign-extended-geotargeting'),
        [OutputCategory.CAMPAIGN_FINISH_TIME]: i18n('campaign-finish-time'),
        [OutputCategory.CAMPAIGN_HIDE]: i18n('campaign-hide'),
        [OutputCategory.CAMPAIGN_MINUS_WORDS]: i18n('campaign-minus-words'),
        [OutputCategory.CAMPAIGN_MOBILE_MULTIPLIER]: i18n('campaign-mobile-multiplier'),
        [OutputCategory.CAMPAIGN_STRATEGY]: i18n('campaign-strategy'),
        [OutputCategory.CAMPAIGN_NETWORK]: i18n('campaign-network'),
        [OutputCategory.CAMPAIGN_PERFORMANCE_TGO_MULTIPLIER]: i18n('campaign-performance-tgo-multiplier'),
        [OutputCategory.CAMPAIGN_REGIONS]: i18n('campaign-regions'),
        [OutputCategory.CAMPAIGN_RETARGETING_MULTIPLIERS]: i18n('campaign-retargeting-multipliers'),
        [OutputCategory.CAMPAIGN_SHOW]: i18n('campaign-show'),
        [OutputCategory.CAMPAIGN_START_TIME]: i18n('campaign-start-time'),
        [OutputCategory.CAMPAIGN_TIME_TARGET]: i18n('campaign-time-target'),
        [OutputCategory.CAMPAIGN_UNARCHIVED]: i18n('campaign-unarchived'),
        [OutputCategory.CAMPAIGN_RESTRICT_FREQUENCY]: i18n('campaign-restrict-frequency'),
        [OutputCategory.CAMPAIGN_VIDEO_MULTIPLIER]: i18n('campaign-video-multiplier'),
        [LogEventCategories.campaignStatus]: i18n('campaign-status'),
        [LogEventCategories.bannerStatus]: i18n('banner-status')
    };

    static getLocalizedString(name: 'on' | 'off' | 'yandex'): string {
        switch (name) {
            case 'on':
                return i18n('on');
            case 'off':
                return i18n('off');
            case 'yandex':
                return i18n('yandex');
            default:
                return name;
        }
    }

    static getCategoryNameFromEnumString(name: Category): string {
        const text = LogHelpers.categoryNames[name];

        if (!text) {
            throw Error(`Неизвестное название категории ${name}`);
        }

        return text;
    }

    static getUserLogPeriods(period: LogPeriod, refDate: moment.Moment): { dateFrom: string; dateTo: string } | {} {
        let dates;
        if (!period.preset && !period.dateFrom && !period.dateTo) {
            return {};
        }
        if (period.preset && !period.dateFrom && !period.dateTo) {
            dates = mapDateRangePresetToDatePeriod(refDate, period.preset);
        } else {
            dates = {
                dateFrom: period.dateFrom ? moment(period.dateFrom) : refDate,
                dateTo: period.dateTo ? moment(period.dateTo) : refDate
            };
        }

        return {
            dateFrom: dates.dateFrom
                .clone()
                .hour(0)
                .minutes(0)
                .utc()
                .format('YYYY-MM-DDTHH:mm:00'),
            dateTo: dates.dateTo
                .clone()
                .hour(23)
                .minutes(59)
                .utc()
                .format('YYYY-MM-DDTHH:mm:59')
        };
    }

    static parseEntities = (criteria: Criteria[], logParams?: LogNavEntityParams): FilterPartialExpression[] => {
        if (!logParams) {
            return [];
        }
        const paramsNames = Object.keys(logParams);
        return paramsNames.reduce((res: FilterPartialExpression[], paramKey) => {
            const param = logParams[paramKey];
            if (param) {
                switch (paramKey) {
                    case 'cid':
                        res.push({
                            field: (
                                criteria.find(item => {
                                    return item.key === 'cid';
                                }) || { title: 'cid' }
                            ).title,
                            operator: Operator.EQUALS,
                            value: {
                                type: FilterExpressionValueType.numberSet,
                                value: [param]
                            }
                        });
                        break;
                    case 'group':
                        res.push({
                            field: (
                                criteria.find(item => {
                                    return item.key === 'adgroupId';
                                }) || { title: 'adgroupId' }
                            ).title,
                            operator: Operator.EQUALS,
                            value: {
                                type: FilterExpressionValueType.numberSet,
                                value: [param]
                            }
                        });
                        break;
                    case 'banner':
                        res.push({
                            field: (
                                criteria.find(item => {
                                    return item.key === 'bannerId';
                                }) || { title: 'bannerId' }
                            ).title,
                            operator: Operator.EQUALS,
                            value: {
                                type: FilterExpressionValueType.numberSet,
                                value: [param]
                            }
                        });
                        break;
                    default:
                        throw Error(`Невозможно наложить фильтр по сущности ${paramKey}`);
                }
            }
            return res;
        }, []);
    };

    static parseLogParams = (criteria: Criteria[], logParams?: LogParams): FilterPartialExpression[] => {
        if (!logParams) {
            return [];
        }
        const paramsNames = Object.keys(logParams);
        return paramsNames.reduce((res: FilterPartialExpression[], paramKey) => {
            const param = logParams[paramKey];
            if (param) {
                switch (paramKey) {
                    case 'cids':
                        res.push({
                            field: (
                                criteria.find(item => {
                                    return item.key === 'cid';
                                }) || { title: 'cid' }
                            ).title,
                            operator: Operator.EQUALS,
                            value: {
                                type: FilterExpressionValueType.numberSet,
                                value: param
                            }
                        });
                        break;
                    case 'isClient':
                        return [];
                    default:
                        throw Error(`Имплицитный фильтр по полю ${paramKey} не поддерживается`);
                }
            }
            return res;
        }, []);
    };

    static getSign = (multiplier: number): string => {
        return multiplier === LogHelpers.noCorrectionValue ? '' : multiplier > LogHelpers.noCorrectionValue ? '+' : '-';
    };

    /*
    Возвращает ширину колонки "что изменилось"
    Из Local Storage или дефолтную
     */
    static getChangedValueColumnWidth(): number {
        const storage = LocalStorage.getItem(StorageKeyType.logColumnsSettings);

        // Причина этой проверки - DIRECT-83994
        // В localStorage может быть старый формат данных.
        const storedWidth =
            storage &&
            storage[logs] &&
            storage[logs].columnViewInfo &&
            storage[logs].columnViewInfo[changedValueKey].width;

        return storedWidth || defaultChangedValueWidth;
    }

    /*
    Эвристическая функция зависимости максимального кол-ва букв в строке от ширины строки
    Считать "по-честному" (через canvas.rect) получается очень дорого
    У функции есть небольшая погрешность на маленьких цифрах (10-20 символов), но она решается переносом на другую строку
     */
    static getMaxSymbolsByColumnWidth(width?: number): number {
        let w = width;
        if (!w) {
            w = LogHelpers.getChangedValueColumnWidth();
        }
        return Math.ceil((w - 5) / 7.9);
    }

    static calcNotAlwaysNeedExpand(maxLineLength: number, maxDiffLength?: number): boolean {
        return this.calcNeedExpand(false, maxLineLength, maxDiffLength);
    }

    static calcNeedExpand(alwaysNeedExpand: boolean, maxLineLength: number, maxDiffLength?: number): boolean {
        let length = maxDiffLength;
        if (!length) {
            length = LogHelpers.getMaxSymbolsByColumnWidth();
        }
        return alwaysNeedExpand || maxLineLength > length;
    }
}

export const LogInitialState: LogState = {
    rowExpanded: new Map<number, boolean>(),
    rowsCanExpand: new Map<number, boolean>(),
    gridData: {
        total: 100,
        data: [],
        allItemsIds: []
    },
    nextPageToken: null,
    columnsSettings: getColumnsSettings(),
    login: '',
    isClient: true,
    allExpanded: false,
    isHorizontalScrollVisible: true,
    isHeaderVisible: true,
    changedValueWidth: LogHelpers.getChangedValueColumnWidth(),
    maxHeightCleared: false
};
